import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { FiSearch, FiFilter } from "react-icons/fi";
import "../css/cards.css";

const sessionShape = PropTypes.shape({
  sessionId: PropTypes.string,
  displayTime: PropTypes.string,
  queryCount: PropTypes.number,
  snippet: PropTypes.string,
  concepts: PropTypes.arrayOf(PropTypes.string),
  questions: PropTypes.arrayOf(PropTypes.string),
  solutionLevel: PropTypes.string,
  solutionType: PropTypes.string,
});

const StudentProfiles = ({ profiles, onInspectSession, onDownloadSession }) => {
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [focusFilter, setFocusFilter] = useState("all");
  const [activeDayDetail, setActiveDayDetail] = useState(null);
  const [sessionInspectingId, setSessionInspectingId] = useState("");
  const [sessionDownloadId, setSessionDownloadId] = useState("");
  const [sessionInspectError, setSessionInspectError] = useState("");

  const formatLocalDayLabel = useCallback((day = {}) => {
    if (day?.events?.length) {
      const eventDate = new Date(day.events[0]);
      if (!Number.isNaN(eventDate.getTime())) {
        return eventDate.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        });
      }
    }
    return day?.label || day?.date || "Activity";
  }, []);

  const hasLocalActivityWithinDays = useCallback((timeline = [], days = 1) => {
    if (!timeline.length || !days) return false;
    const now = new Date();
    const threshold = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return timeline.some((day) =>
      (day.events || []).some((event) => {
        const eventDate = new Date(event);
        return (
          !Number.isNaN(eventDate.getTime()) && eventDate >= threshold
        );
      })
    );
  }, []);

  const safeProfiles = useMemo(
    () => (Array.isArray(profiles) ? profiles : []),
    [profiles]
  );

  const renderActivitySparkline = useCallback((timeline = []) => {
    if (!timeline.length) return null;
    const counts = timeline.map((point) => point?.queries || 0);
    const max = Math.max(...counts, 1);
    const points = counts.map((count, index) => {
      const x =
        timeline.length === 1 ? 0 : (index / (timeline.length - 1)) * 100;
      const y = 30 - (count / max) * 30;
      return `${x},${y}`;
    });
    return (
      <svg viewBox="0 0 100 30" className="profile-activity-sparkline">
        <polyline points={points.join(" ")} />
      </svg>
    );
  }, []);

  const filteredProfiles = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return safeProfiles.filter((profile) => {
      const fullName = `${profile.name || ""} ${profile.username || ""}`
        .toLowerCase()
        .trim();
      const searchMatch =
        normalizedSearch.length === 0 || fullName.includes(normalizedSearch);
      const engaged = Number(profile.todayQueries ?? 0) > 0;
      const statusMatch =
        statusFilter === "all" ||
        (statusFilter === "engaged" && engaged) ||
        (statusFilter === "needs-support" && !engaged);
      const hasAlerts = (profile.alerts || []).length > 0;
      const hintTotal = Number(profile.hintCount ?? 0);
      const answerTotal = Number(profile.fullAnswerCount ?? 0);
      const responseTotal = hintTotal + answerTotal;
      const heavyHints = responseTotal > 0 && hintTotal / responseTotal >= 0.5;
      const focusMatch =
        focusFilter === "all" ||
        (focusFilter === "alerts" && hasAlerts) ||
        (focusFilter === "heavy-hints" && heavyHints);
      return searchMatch && statusMatch && focusMatch;
    });
  }, [safeProfiles, searchTerm, statusFilter, focusFilter]);

  const openProfile = (profile) => {
    setActiveDayDetail(null);
    setSelectedProfile(profile);
    setSessionInspectError("");
    setSessionInspectingId("");
    setSessionDownloadId("");
  };

  const closeProfile = () => {
    setSelectedProfile(null);
    setActiveDayDetail(null);
    setSessionInspectError("");
    setSessionInspectingId("");
    setSessionDownloadId("");
  };

  const handleDayClick = useCallback(
    (day) => {
      if (!day) return;
      const eventList = (day.events || []).map((event) => {
        const dateObj = new Date(event);
        if (Number.isNaN(dateObj.getTime())) {
          return event;
        }
        return dateObj.toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
      });
      setSessionInspectError("");
      setActiveDayDetail({
        label: formatLocalDayLabel(day),
        queries: day.queries || 0,
        events: eventList,
        sessions: day.sessions || [],
      });
    },
    [formatLocalDayLabel]
  );

  const handleSessionInspect = useCallback(
    async (session, source = "timeline") => {
      if (
        !session ||
        !session.sessionId ||
        typeof onInspectSession !== "function" ||
        !selectedProfile
      ) {
        return;
      }
      setSessionInspectError("");
      const sessionKey = `${source}-${session.sessionId}`;
      setSessionInspectingId(sessionKey);
      try {
        await Promise.resolve(
          onInspectSession({
            sessionId: session.sessionId,
            sessionOwner: selectedProfile.username,
            studentName: selectedProfile.name,
            snippet: session.snippet,
          })
        );
      } catch (error) {
        const message =
          error?.message || "Unable to load this session conversation.";
        setSessionInspectError(message);
      } finally {
        setSessionInspectingId("");
      }
    },
    [onInspectSession, selectedProfile]
  );

  const handleSessionDownload = useCallback(
    async (session, source = "timeline") => {
      if (
        !session ||
        !session.sessionId ||
        typeof onDownloadSession !== "function" ||
        !selectedProfile
      ) {
        return;
      }
      setSessionInspectError("");
      const downloadKey = `${source}-${session.sessionId}-download`;
      setSessionDownloadId(downloadKey);
      try {
        await Promise.resolve(
          onDownloadSession({
            sessionId: session.sessionId,
            sessionOwner: selectedProfile.username,
            studentName: selectedProfile.name,
          })
        );
      } catch (error) {
        const message =
          error?.message || "Unable to download this transcript right now.";
        setSessionInspectError(message);
      } finally {
        setSessionDownloadId("");
      }
    },
    [onDownloadSession, selectedProfile]
  );

  return (
    <>
      <div className="chart-card">
        <div className="chart-card-heading">
          <h3 className="chart-title">Student Profiles</h3>
          <span className="profile-count">
            {filteredProfiles.length} of {safeProfiles.length} shown
          </span>
        </div>
        <div className="profiles-controls">
          <div className="profiles-search">
            <FiSearch className="profiles-search-icon" aria-hidden="true" />
            <input
              type="search"
              name="student-search"
              placeholder="Search by name or username"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <div className="profiles-filters">
            <label className="profiles-filter-group">
              <span>Status</span>
              <div className="profiles-filter-control">
                <FiFilter aria-hidden="true" />
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="all">All</option>
                  <option value="engaged">Engaged</option>
                  <option value="needs-support">Needs follow-up</option>
                </select>
              </div>
            </label>
            <label className="profiles-filter-group">
              <span>Focus</span>
              <div className="profiles-filter-control">
                <FiFilter aria-hidden="true" />
                <select
                  value={focusFilter}
                  onChange={(event) => setFocusFilter(event.target.value)}
                >
                  <option value="all">All students</option>
                  <option value="alerts">Only alerts</option>
                  <option value="heavy-hints">Heavy hints</option>
                </select>
              </div>
            </label>
          </div>
        </div>
        {filteredProfiles.length === 0 ? (
          <p className="student-profiles-empty">
            {safeProfiles.length === 0
              ? "No student profiles are available yet."
              : "No students match the current filters."}
          </p>
        ) : (
          <div className="recent-activity-list">
            {filteredProfiles.map((profile, index) => {
              const engagedRecently =
                hasLocalActivityWithinDays(profile.activityTimeline, 3) ||
                Number(profile.todayQueries ?? 0) > 0;
              const needsFollowUp = !engagedRecently;
              const topConcept = profile.concepts?.[0];
              return (
                <button
                  key={`profile-${index}`}
                  type="button"
                  className="activity-item-small profile-clickable"
                  onClick={() => openProfile(profile)}
                >
                  <div className="activity-avatar-small">
                    {profile.name?.charAt(0) || "S"}
                  </div>
                  <div className="activity-info">
                    <p className="activity-name">{profile.name}</p>
                    <p className="activity-action">
                      {profile.lastActive
                        ? `Last active ${profile.lastActive}`
                        : "No recent activity logged"}
                    </p>
                    <div className="profile-activity-meta">
                      <span>
                        {profile.todayQueries ?? 0} today ·{" "}
                        {profile.weeklyQueries ?? 0} this week
                      </span>
                      <span
                        className={`profile-status-pill ${
                          needsFollowUp ? "inactive" : "active"
                        }`}
                      >
                        {needsFollowUp ? "Needs follow-up" : "Engaged"}
                      </span>
                    </div>
                    <div className="profile-card-footer">
                      <div className="profile-concepts-row">
                        {topConcept ? (
                          <span className="profile-concept-chip">
                            {topConcept.concept}
                            <small>{topConcept.count}</small>
                          </span>
                        ) : (
                          <span className="profile-concept-chip muted">
                            No struggles logged
                          </span>
                        )}
                      </div>
                      {profile.activityTimeline?.length ? (
                        <div className="profile-activity-trend">
                          {renderActivitySparkline(profile.activityTimeline)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <span className="activity-time-small">
                    {profile.sessions ?? 0} sessions · Tap for report
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selectedProfile && (
        <div className="dashboard-modal-overlay" onClick={closeProfile}>
          <div
            className="dashboard-modal student-profile-modal"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="dashboard-modal-header">
              <div>
                <p className="modal-eyebrow">
                  {selectedProfile.username || "Student"}
                </p>
                <h3>{selectedProfile.name}</h3>
                <p className="modal-subtitle">
                  Last active {selectedProfile.lastActive || "N/A"}
                </p>
              </div>
              <button
                type="button"
                className="modal-close-button"
                onClick={closeProfile}
              >
                Close
              </button>
            </div>
            <div className="dashboard-modal-body">
              <div className="student-report-grid">
                <div className="student-report-card">
                  <h4>Activity Snapshot</h4>
                  <div className="student-profile-stats">
                    <div>
                      <span className="profile-stat-label">Weekly Queries</span>
                      <strong>{selectedProfile.weeklyQueries ?? 0}</strong>
                    </div>
                    <div>
                      <span className="profile-stat-label">Today</span>
                      <strong>{selectedProfile.todayQueries ?? 0}</strong>
                    </div>
                    <div>
                      <span className="profile-stat-label">Total Sessions</span>
                      <strong>{selectedProfile.sessions ?? 0}</strong>
                    </div>
                    <div>
                      <span className="profile-stat-label">Follow-ups</span>
                      <strong>{selectedProfile.followUps ?? 0}</strong>
                    </div>
                    <div>
                      <span className="profile-stat-label">Multi-turn</span>
                      <strong>{selectedProfile.multiTurnSessions ?? 0}</strong>
                    </div>
                    <div>
                      <span className="profile-stat-label">Total Queries</span>
                      <strong>{selectedProfile.totalQueries ?? 0}</strong>
                    </div>
                  </div>
                </div>
                <div className="student-report-card">
                  <h4>Concept Focus</h4>
                  <div className="student-concept-chips">
                    {selectedProfile.concepts?.length ? (
                      selectedProfile.concepts.map((concept) => (
                        <span
                          className="profile-concept-chip"
                          key={`${selectedProfile.username}-${concept.concept}`}
                        >
                          {concept.concept}
                          <small>{concept.count}</small>
                        </span>
                      ))
                    ) : (
                      <p className="dashboard-modal-empty">
                        No struggling concepts logged yet.
                      </p>
                    )}
                  </div>
                  <div className="student-alerts-block">
                    <p className="profile-stat-label">Alerts</p>
                    {(selectedProfile.alerts || []).length ? (
                      <div className="profile-alert-chip-row">
                        {selectedProfile.alerts.map((alert, idx) => (
                          <span
                            className="profile-alert-chip"
                            key={`alert-${idx}`}
                          >
                            {alert}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="dashboard-modal-empty compact">
                        No alert triggers.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="student-history-section">
                <h4>Recent Activity</h4>
                {selectedProfile.activityTimeline?.length ? (
                  (() => {
                    const timeline = selectedProfile.activityTimeline || [];
                    const heatmapColumns =
                      timeline.reduce(
                        (max, day) => Math.max(max, (day.weekIndex ?? 0) + 1),
                        0
                      ) || 1;
                    const weekdays = [
                      "Sun",
                      "Mon",
                      "Tue",
                      "Wed",
                      "Thu",
                      "Fri",
                      "Sat",
                    ];
                    return (
                      <div className="activity-heatmap-wrapper">
                        <div className="activity-weekday-column">
                          {weekdays.map((dayLabel) => (
                            <span key={`weekday-${dayLabel}`}>{dayLabel}</span>
                          ))}
                        </div>
                        <div
                          className="student-activity-heatmap"
                          style={{
                            gridTemplateColumns: `repeat(${heatmapColumns}, 1fr)`,
                          }}
                        >
                          {timeline.map((day) => {
                            const intensity = Math.min(day.queries || 0, 6);
                            return (
                              <button
                                type="button"
                                key={`activity-${day.date}`}
                                className={`activity-heat-cell level-${intensity}`}
                                style={{
                                  gridRow: (day.weekday ?? 0) + 1,
                                  gridColumn: (day.weekIndex ?? 0) + 1,
                                }}
                                title={`${formatLocalDayLabel(day)}: ${
                                  day.queries || 0
                                } queries`}
                                onClick={() => handleDayClick(day)}
                              >
                                <small>{day.queries || 0}</small>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <p className="dashboard-modal-empty">
                    No activity has been recorded this week.
                  </p>
                )}
                <div className="activity-detail-card">
                  <p className="detail-title">
                    {activeDayDetail
                      ? `${activeDayDetail.label} · ${
                          activeDayDetail.queries
                        } ${
                          activeDayDetail.queries === 1 ? "query" : "queries"
                        }`
                      : "Select a day for timestamps"}
                  </p>
                  {activeDayDetail ? (
                    <div className="activity-detail-columns">
                      <div className="activity-detail-column">
                        <p className="detail-column-label">Access times</p>
                        {activeDayDetail.events?.length ? (
                          <ul className="activity-detail-list">
                            {activeDayDetail.events.map((entry, idx) => (
                              <li key={`event-${idx}`}>{entry}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="dashboard-modal-empty compact">
                            No exact timestamps were captured for that day.
                          </p>
                        )}
                      </div>
                      <div className="activity-detail-column">
                        <p className="detail-column-label">Sessions</p>
                            {activeDayDetail.sessions?.length ? (
                              <ul className="activity-session-list">
                                {activeDayDetail.sessions.map((session, idx) => {
                                  const sessionKey = `timeline-${
                                    session.sessionId || idx
                                  }`;
                                  const hasTranscript = Boolean(session.sessionId);
                                  const loading =
                                    hasTranscript &&
                                    sessionInspectingId === sessionKey;
                                  const downloadKey = `timeline-${
                                    session.sessionId
                                  }-download`;
                                  const downloading =
                                    hasTranscript &&
                                    sessionDownloadId === downloadKey;
                                  return (
                                    <li key={`day-session-${sessionKey}`}>
                                      <button
                                        type="button"
                                        className="activity-session-item"
                                    onClick={() =>
                                      handleSessionInspect(
                                        session,
                                        "timeline"
                                      )
                                    }
                                    disabled={loading || !hasTranscript}
                                  >
                                    <div className="activity-session-head">
                                      <div>
                                        <strong>
                                          {session.displayTime ||
                                            session.sessionId ||
                                            "Session"}
                                        </strong>
                                        {session.sessionId ? (
                                          <span>{session.sessionId}</span>
                                        ) : null}
                                      </div>
                                      <span>
                                        {session.queryCount ?? 0}{" "}
                                        {(session.queryCount ?? 0) === 1
                                          ? "turn"
                                          : "turns"}
                                      </span>
                                    </div>
                                    <p>
                                      {session.snippet ||
                                        "No snippet recorded for this session."}
                                    </p>
                                    <span className="activity-session-action">
                                      {!hasTranscript
                                        ? "Transcript unavailable"
                                        : loading
                                        ? "Opening..."
                                        : "View conversation"}
                                    </span>
                                  </button>
                                  {hasTranscript ? (
                                    <button
                                      type="button"
                                      className="session-download-button"
                                      onClick={() =>
                                        handleSessionDownload(
                                          session,
                                          "timeline"
                                        )
                                      }
                                      disabled={downloading || loading}
                                    >
                                      {downloading
                                        ? "Downloading..."
                                        : "Download PDF"}
                                    </button>
                                  ) : null}
                                    </li>
                                  );
                                })}
                              </ul>
                        ) : (
                          <p className="dashboard-modal-empty compact">
                            No sessions were logged for this day.
                          </p>
                        )}
                        {sessionInspectError ? (
                          <p className="activity-session-error">
                            {sessionInspectError}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <p className="dashboard-modal-empty compact">
                      Click a square to see timestamps and session summaries.
                    </p>
                  )}
                </div>
              </div>

              <div className="student-history-section">
                <h4>Session History</h4>
                {selectedProfile.recentSessions?.length ? (
                  <ul className="student-history-list">
                    {selectedProfile.recentSessions.map((session, idx) => {
                      const hasTranscript = Boolean(session.sessionId);
                      const historyKey = `history-${
                        session.sessionId || idx
                      }`;
                      const loading =
                        hasTranscript && sessionInspectingId === historyKey;
                      const downloadKey = `history-${
                        session.sessionId
                      }-download`;
                      const downloading =
                        hasTranscript && sessionDownloadId === downloadKey;
                      return (
                        <li key={`history-${idx}`} className="history-card">
                          <div className="history-card-head">
                            <div>
                              <strong>
                                {session.displayTime || "Session"}
                              </strong>
                              <span>{session.sessionId}</span>
                            </div>
                            <span>{session.queryCount ?? 0} turns</span>
                          </div>
                          <p className="history-snippet">
                            {session.snippet || "No snippet recorded."}
                          </p>
                          <div className="history-meta">
                            <div className="profile-concepts-row">
                              {(session.concepts || []).length ? (
                                session.concepts.map((concept) => (
                                  <span
                                    className="profile-concept-chip secondary"
                                    key={`${session.sessionId}-${concept}`}
                                  >
                                    {concept}
                                  </span>
                                ))
                              ) : (
                                <span className="profile-concept-chip muted">
                                  No concept tagged
                                </span>
                              )}
                            </div>
                            {session.questions?.length ? (
                              <ul className="session-question-list">
                                {session.questions.map((question, qIdx) => (
                                  <li key={`question-${qIdx}`}>{question}</li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            className="history-view-button"
                            onClick={() =>
                              handleSessionInspect(session, "history")
                            }
                            disabled={loading || !hasTranscript}
                          >
                            {!hasTranscript
                              ? "Transcript unavailable"
                              : loading
                              ? "Opening conversation..."
                              : "View conversation"}
                          </button>
                          {hasTranscript ? (
                            <button
                              type="button"
                              className="session-download-button secondary"
                              onClick={() =>
                                handleSessionDownload(session, "history")
                              }
                              disabled={downloading || loading}
                            >
                              {downloading ? "Downloading..." : "Download PDF"}
                            </button>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="dashboard-modal-empty">
                    We haven’t captured any session history for this student.
                  </p>
                )}
              </div>

              <div className="student-history-section">
                <h4>Flagged Issues</h4>
                {selectedProfile.issues && selectedProfile.issues.length > 0 ? (
                  <ul className="profile-issues-list">
                    {selectedProfile.issues.map((issue, idx) => (
                      <li key={`issue-${idx}`}>{issue}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="dashboard-modal-empty">
                    No issues reported for this student.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

StudentProfiles.propTypes = {
  profiles: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      totalQueries: PropTypes.number,
      hintCount: PropTypes.number,
      fullAnswerCount: PropTypes.number,
      issues: PropTypes.arrayOf(PropTypes.string),
      weeklyQueries: PropTypes.number,
      todayQueries: PropTypes.number,
      lastActive: PropTypes.string,
      followUps: PropTypes.number,
      multiTurnSessions: PropTypes.number,
      alerts: PropTypes.arrayOf(PropTypes.string),
      sessions: PropTypes.number,
      activityTimeline: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string,
          label: PropTypes.string,
          queries: PropTypes.number,
          weekday: PropTypes.number,
          weekIndex: PropTypes.number,
          events: PropTypes.arrayOf(PropTypes.string),
          sessions: PropTypes.arrayOf(sessionShape),
        })
      ),
      recentSessions: PropTypes.arrayOf(sessionShape),
      concepts: PropTypes.arrayOf(
        PropTypes.shape({
          concept: PropTypes.string,
          count: PropTypes.number,
        })
      ),
    })
  ),
  onInspectSession: PropTypes.func,
  onDownloadSession: PropTypes.func,
};

export default StudentProfiles;
